import { Injectable } from '@angular/core';

import {
  NodeEntry,
  NodeAssociationEntry,
  NodeChildAssociationEntry,
  ProcessDefinitionRepresentation,
  AppDefinitionRepresentation,
  ProcessInstanceRepresentation,
} from '@alfresco/js-api';
import {
  NodeChildAssociationPaging,
  NodePaging,
} from '@alfresco/js-api/src/api/content-rest-api';
import { Store } from '@ngrx/store';
import {
  Observable,
  BehaviorSubject,
  from,
  of,
  forkJoin,
  firstValueFrom,
} from 'rxjs';
import {
  map,
  mergeMap,
  catchError,
  switchMap,
  take,
  filter,
} from 'rxjs/operators';

import { ContentFilterModel } from '../../expanded-content/expanded-content-filter/models/content-filter.model';
import { getLockIconStyle } from '../../../utils/content.utils';
import { WkSiteModel } from '../../../model/wk-site.model';
import { WkSiteContainerModel } from '../../../model/wk-site-children.model';
import {
  WkTemplate,
  WkTemplateFormRequest,
} from '../../../model/wk-template-model';
import { AppStore } from '../../../store/states/app.state';
import { getUserProfile } from '../../../store/selectors/app.custom-selectors';
import { SplitModeFile } from '../models/split-mode-file.model';
import { WkAppConfigService } from '../../../services/wk-app-config.service';
import { WkAlfrescoApiService } from '../../../services/wk-alfresco-api.service';
import { RequestParams } from '../../../types';
import { NODE_TITLE } from '../../../constants';
import { AppConstants } from '../constants/app-constants';
import {
  EcmUserModel,
  PeopleContentService,
} from '@alfresco/adf-content-services';
import { SortDirection } from '../../../enums';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  public libraryCreated = false;
  public readonly splitModeFile$ = new BehaviorSubject<SplitModeFile>({
    isSplitMode: false,
  });

  private readonly contentServiceConfig = {
    searchApi: 'alfresco/api/-default-/public/search/versions/1/search',
    nodeApi: 'alfresco/api/-default-/public/alfresco/versions/1/nodes',
    templatesApi: 'alfresco/s/wk/nodes',
    templateFormApi: 'alfresco/s/wk/templates',
    nodeContentApi: 'alfresco/s/wk/content',
    siteApi:
      'alfresco/api/-default-/public/alfresco/versions/1/people/-me-/sites?roles=user&size=100',
    getLastSiteLoginApi:
      'alfresco/api/-default-/public/alfresco/versions/1/people/-me-/preferences/lastLogInSite',
    setLastSiteLoginApi: 'alfresco/s/api/people',
    appDefinitionsApi: 'activiti-app/api/enterprise/runtime-app-definitions',
    processInstancesApi: 'activiti-app/api/enterprise/process-instances/query',
    processInstancesContentApi: 'activiti-app/api/enterprise/process-instances',
    formsApi: 'activiti-app/api/enterprise/models',
    contentTypes: ['application/json'],
    accepts: ['application/json'],
    searchAspects: {
      currentSite: '',
      documentType: 'cm:content',
      aspect: 'cm:checkedOut',
      modified: 'cm:modified',
    },
    posterApi: 'alfresco/s/wk/renditions/imgpreview/content',
  };
  private broadcastChannel: BroadcastChannel;
  private readonly filtersData$: BehaviorSubject<ContentFilterModel> =
    new BehaviorSubject<ContentFilterModel>(
      new ContentFilterModel(null, null, null, null)
    );

  constructor(
    private readonly store: Store<AppStore>,
    private readonly appConfigService: WkAppConfigService,
    private readonly ecmUserService: PeopleContentService,
    private readonly apiService: WkAlfrescoApiService
  ) {}

  public getBroadcastChannel(): BroadcastChannel {
    if (this.broadcastChannel) {
      return this.broadcastChannel;
    }
    this.broadcastChannel = new BroadcastChannel('core_channel');
    return this.broadcastChannel;
  }

  public closeBroadcastChannel(): void {
    this.broadcastChannel.close();
    this.broadcastChannel = null;
  }

  public setFiltersData(filtersData: ContentFilterModel): void {
    this.filtersData$.next(filtersData);
  }

  public getFiltersData(): Observable<ContentFilterModel> {
    return this.filtersData$.asObservable();
  }

  public pick(object, keys): any {
    return keys.reduce((obj, key) => {
      if (object && object.hasOwnProperty(key)) {
        obj[key] = object[key];
      }
      return obj;
    }, {});
  }

  public getNode(nodeId: string): Observable<NodeEntry> {
    const include = 'path';
    return from(
      this.apiService.callContentClientCustomApi({
        path: `${this.contentServiceConfig.nodeApi}/${nodeId}?include=${include}`,
        httpMethod: 'GET',
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    );
  }

  public getNodeContent(nodeId: string): Observable<any> {
    return from(
      this.apiService.callContentClientCustomApi({
        path: `${this.contentServiceConfig.nodeApi}/${nodeId}/content`,
        httpMethod: 'GET',
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    );
  }

  public getDocuments(modifier, site): Observable<any> {
    const dayLimit = this.appConfigService.get<number>(
      'widgets.contentWidget.dayLimit'
    );
    return this.ecmUserService.getCurrentUserInfo().pipe(
      take(1),
      switchMap((userInfo: EcmUserModel) => {
        const modifierName = modifier === 'My' ? userInfo.id : '*';
        const currentSite = `cm:${site}/cm:documentLibrary`;
        const httpParams = {
          query: {
            query: `PATH:'app:company_home/st:sites/${currentSite}//*'
                AND @cm:modifier:${modifierName}
                AND TYPE:'${this.contentServiceConfig.searchAspects.documentType}'
                AND -ASPECT:'${this.contentServiceConfig.searchAspects.aspect}'
                AND -ASPECT:'sys:hidden'
                AND @cm:modified:[NOW-${dayLimit}DAYS TO NOW]`,
            language: 'afts',
          },
          sort: [
            {
              type: 'FIELD',
              field: this.contentServiceConfig.searchAspects.modified,
              ascending: 'false',
            },
          ],
          paging: {
            maxItems: 7,
          },
          include: ['aspectNames'],
        };
        return from(
          this.apiService.callContentClientCustomApi({
            path: this.contentServiceConfig.searchApi,
            httpMethod: 'POST',
            bodyParam: httpParams,
            contentTypes: this.contentServiceConfig.contentTypes,
            accepts: this.contentServiceConfig.accepts,
          })
        );
      }),
      map((data) => data['list'].entries.map((item) => item['entry']))
    );
  }

  public allContentSorting(key, direction, modifier, site): Promise<any> {
    const dayLimit = this.appConfigService.get<number>(
      'widgets.contentWidget.dayLimit'
    );
    let userName;
    const userProfile$ = this.store.select(getUserProfile).pipe(
      take(1),
      switchMap((userData) => {
        userName = userData.id;
        const currentSite = `cm:${site}/cm:documentLibrary`;
        const modifierName = modifier === 'My' ? userName : '*';
        const ascending = direction === 'desc' ? 'false' : 'true';
        let sortKey;
        switch (key) {
          case 'name':
            sortKey = 'cm:name';
            break;
          case 'createdAt':
            sortKey = 'cm:created';
            break;
          case 'modifiedAt':
            sortKey = this.contentServiceConfig.searchAspects.modified;
            break;
          default:
            sortKey = this.contentServiceConfig.searchAspects.modified;
        }
        const httpParams = {
          query: {
            query: `PATH:'app:company_home/st:sites/${currentSite}//*'
                AND @cm:modifier:${modifierName}
                AND TYPE:'${this.contentServiceConfig.searchAspects.documentType}'
                AND -ASPECT:'${this.contentServiceConfig.searchAspects.aspect}'
                AND -ASPECT:'sys:hidden'
                AND @cm:modified:[NOW-${dayLimit}DAYS TO NOW]`,
            language: 'afts',
          },
          sort: [{ type: 'FIELD', field: sortKey, ascending }],
          paging: {
            maxItems: 7,
          },
          include: ['aspectNames'],
        };
        return from(
          this.apiService.callContentClientCustomApi({
            path: this.contentServiceConfig.searchApi,
            httpMethod: 'POST',
            bodyParam: httpParams,
            contentTypes: this.contentServiceConfig.contentTypes,
            accepts: this.contentServiceConfig.accepts,
          })
        );
      })
    );
    return firstValueFrom(userProfile$)
      .then((data) => data['list'].entries.map((item) => item['entry']))
      .then((data) => data.filter((item) => item['isFile']))
      .catch((err) => console.error(err));
  }

  public getExpandedContentDocuments(
    key,
    direction,
    modifier,
    rowsCount,
    skipCount,
    site,
    filtersData?: ContentFilterModel
  ): Observable<any> {
    return this.store.select(getUserProfile).pipe(
      filter(({ id }) => Boolean(id)),
      take(1),
      map(({ id }) => {
        const dayLimit = this.appConfigService.get<number>(
          'contentOverview.dayLimit'
        );

        const modifierName = modifier === 'My' ? id : '*';
        const ascending = direction === SortDirection.Desc ? 'false' : 'true';
        const currentSite = `cm:${site}/cm:documentLibrary`;
        let sortKey: string;

        switch (key) {
          case 'name':
            sortKey = 'cm:name';
            break;
          case 'createdAt':
            sortKey = 'cm:created';
            break;
          case 'modifiedAt':
            sortKey = this.contentServiceConfig.searchAspects.modified;
            break;
          default:
            sortKey = this.contentServiceConfig.searchAspects.modified;
        }
        const hasFiltersByMDate = this.hasFiltersByModifiedDate(filtersData)
          ? `AND @cm:modified:${this.createDateRange(
              filtersData.modifiedDateFrom,
              filtersData.modifiedDateTo
            )}`
          : ``;
        const hasFiltersByCDate = this.hasFiltersByCreatedDate(filtersData)
          ? `AND @cm:created:${this.createDateRange(
              filtersData.createdDateFrom,
              filtersData.createdDateTo
            )}`
          : ``;
        const hasFilterValues = this.hasFilters(filtersData)
          ? ``
          : `AND @cm:modified:[NOW-${dayLimit}DAYS TO NOW]`;

        return {
          query: {
            query: `PATH:'app:company_home/st:sites/${currentSite}//*'
              AND @cm:modifier:${modifierName}
              AND TYPE:'${this.contentServiceConfig.searchAspects.documentType}'
              AND -ASPECT:'${this.contentServiceConfig.searchAspects.aspect}'
              AND -ASPECT:'sys:hidden'
              ${hasFiltersByMDate}
              ${hasFiltersByCDate}
              ${hasFilterValues}
            `,
            language: 'afts',
          },
          sort: [{ type: 'FIELD', field: sortKey, ascending }],
          paging: {
            maxItems: rowsCount,
            skipCount,
          },
          include: ['properties', 'aspectNames'],
        };
      }),
      switchMap((httpParams) =>
        from(
          this.apiService.callContentClientCustomApi({
            path: this.contentServiceConfig.searchApi,
            httpMethod: 'POST',
            bodyParam: httpParams,
            contentTypes: this.contentServiceConfig.contentTypes,
            accepts: this.contentServiceConfig.accepts,
          })
        )
      ),
      map((data: any) => data.list)
    );
  }

  public formatContentArray(data: Array<any>): Array<any> {
    return data.map((item) => {
      item['createdBy'] =
        item.createdByUser && item.createdByUser.displayName
          ? item.createdByUser.displayName
          : '';
      item['modifiedBy'] =
        item.modifiedByUser && item.modifiedByUser.displayName
          ? item.modifiedByUser.displayName
          : '';
      item['title'] =
        item.properties && item.properties['cm:title']
          ? item.properties['cm:title']
          : '';
      item['content_type'] = item.nodeType;
      item['statusIcon'] = this.getIconStyle(item.aspectNames);
      return item;
    });
  }

  private getIconStyle(lockStatus) {
    return getLockIconStyle(lockStatus);
  }

  private getRuntimeApplications(): Observable<AppDefinitionRepresentation[]> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: this.contentServiceConfig.appDefinitionsApi,
        httpMethod: 'GET',
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    ).pipe(map((response: any) => response.data));
  }

  private findAppByDefinitionName(
    applicationsList: AppDefinitionRepresentation[],
    definitionName: string[]
  ): AppDefinitionRepresentation {
    return applicationsList.find((app: AppDefinitionRepresentation) =>
      definitionName.includes(app.name)
    );
  }

  public getDocumentsAttachedToProcesses(): Observable<any> {
    if (!this.appConfigService.apsEnabled()) {
      return of([]);
    }
    return this.getRuntimeApplications().pipe(
      map((runtimeApplications: AppDefinitionRepresentation[]) => {
        if (!runtimeApplications.length) {
          return null;
        }
        const appDefinitionName =
          this.appConfigService.get<string[]>('appDefinitionName');

        return this.findAppByDefinitionName(
          runtimeApplications,
          appDefinitionName
        );
      }),
      mergeMap((application: AppDefinitionRepresentation) => {
        if (!application) {
          return of([]);
        }
        return this.getApplicationProcesses(application);
      }),
      mergeMap((processes: ProcessDefinitionRepresentation[]) => {
        if (!processes.length) {
          return of([]);
        }
        const processDocumentsRequest = processes.map((process) =>
          this.getProcessDocuments(process)
        );
        return forkJoin(processDocumentsRequest);
      }),
      catchError(() => of([]))
    );
  }

  private getApplicationProcesses(
    application: AppDefinitionRepresentation
  ): Observable<ProcessDefinitionRepresentation[]> {
    const queryParams = {
      appDefinitionId: application.id,
    };
    return from(
      this.apiService.callProcessClientCustomApi({
        path: this.contentServiceConfig.processInstancesApi,
        httpMethod: 'POST',
        bodyParam: queryParams,
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    ).pipe(map((response: any) => response.data));
  }

  private getProcessDocuments(
    process: ProcessInstanceRepresentation
  ): Observable<any> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: `${this.contentServiceConfig.processInstancesContentApi}/${process.id}/field-content`,
        httpMethod: 'GET',
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    ).pipe(
      map((response: any) => {
        if (response.size) {
          const content = response.data[0].content;
          return {
            name: process.processDefinitionName,
            id: content[0].sourceId ? content[0].sourceId.split(';')[0] : 0,
          };
        }
        return null;
      })
    );
  }

  public hasFilters(filtersData: ContentFilterModel): boolean {
    if (
      filtersData &&
      (filtersData.createdDateFrom ||
        filtersData.createdDateTo ||
        filtersData.modifiedDateFrom ||
        filtersData.modifiedDateTo)
    ) {
      return true;
    }
    return false;
  }

  private hasFiltersByCreatedDate(filtersData: ContentFilterModel): boolean {
    if (
      filtersData &&
      (filtersData.createdDateFrom || filtersData.createdDateTo)
    ) {
      return true;
    }
    return false;
  }

  private hasFiltersByModifiedDate(filtersData: ContentFilterModel): boolean {
    if (
      filtersData &&
      (filtersData.modifiedDateFrom || filtersData.modifiedDateTo)
    ) {
      return true;
    }
    return false;
  }

  private createDateRange(
    dateFrom: string | null,
    dateTo: string | null
  ): string {
    let dateFromValue = 'MIN';
    let dateToValue = 'MAX';

    if (dateFrom) {
      const date = new Date(dateFrom);
      dateFromValue = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      ).toISOString();
    }

    if (dateTo) {
      const date = new Date(dateTo);
      dateToValue = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate() + 1)
      ).toISOString();
    }

    return `[${dateFromValue} TO ${dateToValue}]`;
  }

  /**
   * Returns the sites visible to the current user.
   */
  public getSites(): Promise<WkSiteModel[]> {
    const requestParam: RequestParams = {
      path: this.contentServiceConfig.siteApi,
      httpMethod: 'GET',
      contentTypes: this.contentServiceConfig.contentTypes,
      accepts: this.contentServiceConfig.accepts,
    };
    return this.apiService
      .callContentClientCustomApi(requestParam)
      .then((data: any) =>
        data.list.entries.map((item: any) => ({
          id: item.entry.id,
          title: item.entry.site.title,
          guid: item.entry.guid,
          role: item.entry.role,
        }))
      )
      .catch(() => null);
  }

  public getTemplateList(folderId: string): Observable<WkTemplate[]> {
    return from(
      this.apiService.callContentClientCustomApi({
        path: `${this.contentServiceConfig.templatesApi}/${folderId}/templates`,
        httpMethod: 'GET',
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    ).pipe(map((data: any) => this.getData(data)));
  }

  public getTemplateForm(templateId: string) {
    return from(
      this.apiService.callContentClientCustomApi({
        path: `${this.contentServiceConfig.templateFormApi}/${templateId}/form/content`,
        httpMethod: 'GET',
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    );
  }

  private getData(response: any): WkTemplate[] {
    return response.list.entries.map((entry: any) => ({
      name: entry.name,
      id: entry.id,
      title: entry.properties['cm:title'],
    }));
  }

  public getSubTemplateContent(
    nodeId: string,
    subTemplateId: string
  ): Observable<any> {
    return from(
      this.apiService.callContentClientCustomApi({
        path: `${this.contentServiceConfig.templatesApi}/${nodeId}/subtemplates/${subTemplateId}/content`,
        httpMethod: 'GET',
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    );
  }

  public createTemplate(
    folderId: string,
    templateId: string,
    requestBody: WkTemplateFormRequest
  ): Observable<string> {
    return from(
      this.apiService.callContentClientCustomApi({
        path: `${this.contentServiceConfig.templatesApi}/${folderId}/createfrom/${templateId}`,
        httpMethod: 'POST',
        bodyParam: requestBody,
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    ).pipe(
      map((data: any) => {
        if (typeof data === 'string') {
          return JSON.parse(data).id;
        }
        return data.id;
      })
    );
  }

  /**
   * Returns a list of children(site containers) of a given site.
   *
   * @param siteguid guid of the current site
   * @param options query params related to the api
   * @see {@link https://api-explorer.alfresco.com/api-explorer/#/nodes/listNodeChildren} for further information
   */
  public getSiteContainers(
    siteguid: string,
    options = {
      include: ['properties', 'path', 'aspectNames'],
      where: '(isFolder=true)',
    }
  ): Observable<WkSiteContainerModel[]> {
    return from(
      this.apiService.nodesApi.listNodeChildren(siteguid, options)
    ).pipe(
      map((data: NodeChildAssociationPaging) =>
        data.list.entries.map((item: NodeChildAssociationEntry) => ({
          id: item.entry.id,
          name: item.entry.name,
          title: item.entry.properties[NODE_TITLE],
          entry: item.entry,
        }))
      )
    );
  }

  public getLastSiteLoggedIn(): Observable<string> {
    return from(
      this.apiService.callContentClientCustomApi({
        path: this.contentServiceConfig.getLastSiteLoginApi,
        httpMethod: 'GET',
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    ).pipe(
      catchError(() =>
        of({
          entry: {
            value: 'no-site',
          },
        })
      ),
      map((data: any) => data.entry.value)
    );
  }

  public setLastloginSite(userName: string, siteId: string): Observable<any> {
    const requestBody = {
      lastLogInSite: siteId,
    };
    return from(
      this.apiService.callContentClientCustomApi({
        path: `${this.contentServiceConfig.setLastSiteLoginApi}/${userName}/preferences`,
        httpMethod: 'POST',
        bodyParam: requestBody,
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    );
  }

  public getFormId(filterType: string, formName: string): Observable<string> {
    const queryParams = {
      filter: filterType,
      filterText: formName,
      modelType: 2,
    };
    return from(
      this.apiService.callProcessClientCustomApi({
        path: this.contentServiceConfig.formsApi,
        httpMethod: 'GET',
        queryParams,
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    ).pipe(
      map((data: any) => {
        if (data.data.length === 0) {
          return 'no-template-form';
        }
        return data.data[0].id;
      }),
      catchError(() => of('template-form-name'))
    );
  }

  public getMediaFolder(...theArgs): Observable<NodePaging> {
    let folderPath = '';
    theArgs.forEach((item) => (folderPath += `${item}`));
    const httpParams = {
      query: {
        query: `PATH:'${folderPath}' AND TYPE:'cm:folder'`,
        language: 'afts',
      },
      include: ['path'],
    };
    return from(
      this.apiService.callContentClientCustomApi({
        path: this.contentServiceConfig.searchApi,
        httpMethod: 'POST',
        bodyParam: httpParams,
        contentTypes: this.contentServiceConfig.contentTypes,
        accepts: this.contentServiceConfig.accepts,
      })
    );
  }

  public setSplitModeFile(splitMode: SplitModeFile): void {
    this.splitModeFile$.next(splitMode);
  }

  public getNodeContentUrl(tenantId: string): string {
    const ecmHost = this.appConfigService.get('ecmHost');
    return `${ecmHost}/${this.contentServiceConfig.nodeContentApi}?tenantId=${tenantId}`;
  }

  public getWorkingCopy(
    nodeId: string
  ): Observable<NodeAssociationEntry | null> {
    return from(
      this.apiService.nodesApi.listTargetAssociations(nodeId, {
        where: `(assocType=${AppConstants.WORKING_COPY_LINK})`,
      })
    ).pipe(
      map(({ list: { entries } }) => this.findWorkingCopy(entries) || null)
    );
  }

  private findWorkingCopy(
    entries: NodeAssociationEntry[]
  ): NodeAssociationEntry {
    return entries.find(
      ({ entry }) =>
        entry.association.assocType === AppConstants.WORKING_COPY_LINK
    );
  }

  public getContentDetailsComponentId(nodeType: string): {
    componentId: string;
    tabId: string;
  } {
    const nodeMetadataMapping = this.appConfigService.get(
      'nodeType-metadata-mapping'
    );
    if (nodeMetadataMapping) {
      const { tabId } = nodeMetadataMapping['default'] || {};
      const componentObj = {
        componentId: '',
        tabId,
      };
      if (nodeMetadataMapping[nodeType]?.component) {
        return {
          ...componentObj,
          componentId: nodeMetadataMapping[nodeType].component,
        };
      } else {
        return componentObj;
      }
    }
    return null;
  }

  public getPosterUrl(tenantId: string): string {
    const isSsoLoggedIn = localStorage.getItem('isSsoUser') === 'true';
    if (isSsoLoggedIn) {
      return `${this.appConfigService.get('ecmHost')}/${
        this.contentServiceConfig.posterApi
      }?tenantId=${tenantId}`;
    } else {
      return `${this.appConfigService.get('ecmHost')}/${
        this.contentServiceConfig.posterApi
      }?tenantId=${tenantId}&alf_ticket=${localStorage.getItem('ticket-ECM')}`;
    }
  }
}
